import { useEffect, useState } from 'react';
import { Col, Form, message } from 'antd';
import { BookyButton } from '../Buttons/BookyButton';
import { UserAddOutlined } from '@ant-design/icons';
import { TextService } from '../../Services/TextService';
import { MemberService } from '../../Services/MemberService';
import { WebApiServicesDataTransferObjectsGroupServiceGroupResultDto, WebApiMemberDtoMemberDto, WebApiMemberDtoCustomMemberFieldValueDto, WebApiModelsMemberField, WebApiModelsMembershipProduct } from '../../Repository/eventbookyapi';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';
import { OneFrontModal } from '../Modals/OneFrontModal';
import { GroupType } from '../../Types/GroupType';
import { OutPutMemberEditFields } from '../Forms/OutPutMemberFields';
import { MembershipProductService } from '../../Services/MembershipProductService';

interface AddMemberProps {
    members: WebApiMemberDtoMemberDto[];
    groups: WebApiServicesDataTransferObjectsGroupServiceGroupResultDto[];
    membershipProducts: WebApiModelsMembershipProduct[];
    memberFieldSettings: WebApiModelsMemberField[];
    user: { hasOnlyReadRights: boolean; maxMembers: number };
    nextMemberIdSuggestion: string;
    loadMembers: () => void;
}

export const AddMember: React.FC<AddMemberProps> = (props) => {
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false as boolean);

    const GetText = TextService.GetText;

    const onCancel = () => {
        form.setFieldValue("groupName", "");
        setOpen(false);
    };

    useEffect(() => {
        let groups: any = [];
        let defaultGroups: number[] = [];

        props.groups.forEach((group: WebApiServicesDataTransferObjectsGroupServiceGroupResultDto) => {
            if (!group.isDefaultGroup) {
                groups.push({ value: group.id, label: group.name });

                if (group.groupType == GroupType.Default) {
                    defaultGroups.push(group.id!);
                }
            }
        });
        setGroups(groups);
        form.resetFields();
        form.setFieldValue("uniqueId", props.nextMemberIdSuggestion);
        form.setFieldValue("groups", defaultGroups);
    }, [props])

    const setFieldCustomError = (fieldName: string, error: string,) => {
        form.setFields([
            {
                name: fieldName, // required
                errors: [error],
            },
        ]);
    }

    const onCreate = async (values: any) => {
        setLoading(true);

        console.log(values);

        let foundError = false;
        props.members.forEach((member: WebApiMemberDtoMemberDto) => {
            if (member.uniqueId == values.uniqueId) {
                setFieldCustomError("uniqueId", "Finns redan en medlem med id " + member.uniqueId);
                foundError = true;
            }

            if (values.email?.length > 0 && member.email == values.email) {
                setFieldCustomError("email", "Finns redan en medlem med epost " + member.email)
                foundError = true;
            }
        });

        if (props.members.length >= props.user.maxMembers) {
            setFieldCustomError("email", GetText("AddAdmin_MaxAllowedMembers"))
            foundError = true;
        }

        if (foundError) {
            setLoading(false);
            return true;
        }

        var memberProducts = [];

        // Combine membership and membershipAddon into memberProducts array
        if (values.membership) {
            memberProducts.push(values.membership);
        }

        if (values.membershipAddon && Array.isArray(values.membershipAddon)) {
            memberProducts.push(...values.membershipAddon);
        }

        const customFields: WebApiMemberDtoCustomMemberFieldValueDto[] = [];

        props.memberFieldSettings.forEach((field: WebApiModelsMemberField) => {
            if (field.columnType === MemberFieldTypes.Custom) {
                customFields.push({
                    fieldId: field.id!,
                    fieldValue: values[field.id!]
                });
            }
        });

        try {
            await MemberService.AddMember({
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                email2: values.email2,
                phoneNumber: values.phoneNumber,
                address: values.address,
                postalArea: values.postalArea,
                postalCode: values.postalCode,
                country: values.country,
                company: values.company,
                uniqueId: values.uniqueId,
                groups: values.groups,
                title: values.title,
                personalNumber: values.personalNumber,
                adressRow2: values.adressRow2,
                adressRow3: values.adressRow3,
                webPage: values.webPage,
                joinedDate: values.joinedDate,
                extra1: values.extra1,
                extra2: values.extra2,
                memberProducts: memberProducts,
                extra3: values.extra3,
                customFieldValues: customFields,
            });
        } catch (error) {
            message.warning(GetText("AddMember_ErrorSaving"));
            return;
        }
        setLoading(false);
        setOpen(false);

        props.loadMembers();
    }

    const onPhoneChange = (value: any) => {
        form.setFieldValue("phoneNumber", value);
    }

    return (
        <>
            <OneFrontModal
                open={open}
                title={GetText("AddMember_Title")}
                okText={GetText("AddMember_Ok")}
                okButtonProps={{ 'data-testid': 'create-member-button' }}
                confirmLoading={loading}
                cancelText={GetText("AddMember_Cancel")}
                onCancel={onCancel}
                onOk={() => {
                    form
                        .validateFields()
                        .then((values) => {
                            onCreate(values);
                        })
                        .catch((info) => {
                            console.log('Validate Failed:', info);
                        });
                }}
            >
                <Form colon={false} form={form} labelAlign={"left"} layout="vertical" onFinish={onCreate}>
                    <Col span={24}>
                        <OutPutMemberEditFields
                            memberFieldSettings={props.memberFieldSettings}
                            membershipProducts={props.membershipProducts}
                            GetText={GetText}
                            groups={groups}
                            user={props.user}
                            onPhoneChange={onPhoneChange}
                            isAdmin={true}
                        />
                    </Col>
                </Form>
            </OneFrontModal>

            <BookyButton
                data-testid="add-member-button"
                type="primary"
                disabled={props.user.hasOnlyReadRights}
                fullWidth={true}
                text={GetText("AddMember_AddButton")}
                onClick={() => {

                    setOpen(true);
                }}
                icon={<UserAddOutlined />}
            >
            </BookyButton>
        </>
    );
};

export default AddMember;